import axios from 'axios'
import router from '../router'
import { useAuthStore } from '../stores/auth-store'

export const AUTH_TOKEN_STORAGE_KEY = 'auth_token'

export const getStoredToken = () => localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)
export const setStoredToken = (token: string) => localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token)
export const clearStoredToken = () => localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)

const http = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    Accept: 'application/json',
  },
  // No timeout was ever set here before — meaning a genuine network stall (not just a slow
  // response, but one that never comes back: a dropped connection, a backend deadlock, ...) left
  // whatever `isLoading` ref was watching that request stuck `true` forever, with no error ever
  // surfacing to reject the promise. Found while chasing a "the customers table spins forever"
  // report — that specific case turned out to be a render crash from a response-shape mismatch
  // (see loyalty.ts's `normalizeCustomersList`), not actually this, but the app had zero
  // protection against the literal network-hang version of the same symptom either. 15s is
  // generous for anything this app calls today; a real timeout now rejects with a clear message
  // (see `getApiErrorMessage` below) instead of hanging indefinitely.
  timeout: 15000,
})

http.interceptors.request.use((config) => {
  const token = getStoredToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // clearSession() resets BOTH localStorage and this store's own in-memory `token`/`user` —
      // clearing only localStorage (as this used to do via clearStoredToken() directly) left
      // `isAuthenticated` reading stale in-memory state, which made the router guard bounce the
      // redirect below straight back to /dashboard instead of actually landing on /login. See the
      // comment on clearSession() in stores/auth-store.ts for the full loop this caused.
      useAuthStore().clearSession()
      if (router.currentRoute.value.name !== 'login') {
        router.push({ name: 'login' })
      }
    }
    return Promise.reject(error)
  },
)

/** Extracts a user-facing message from an API error (e.g. Laravel's `{ message }` body), with a fallback. */
export const getApiErrorMessage = (error: unknown, fallback = 'Something went wrong. Please try again.') => {
  if (axios.isAxiosError(error)) {
    // A timed-out request has no `.response` at all (nothing ever came back to read a message
    // from) — worth naming specifically rather than falling through to each caller's generic
    // "couldn't load X" text, since "it took too long" is a different, more actionable thing for
    // a user to read than "something went wrong".
    if (error.code === 'ECONNABORTED') {
      return 'The request took too long to respond. Please check your connection and try again.'
    }
    return (error.response?.data as { message?: string } | undefined)?.message ?? fallback
  }
  return fallback
}

/**
 * Extracts per-field validation messages from a Laravel-style 422 response
 * (`{ message, errors: { field: [msg, ...] } }`). Returns `{}` for any other error shape (a
 * generic failure, a non-validation error, etc.) — callers fall back to `getApiErrorMessage`
 * for those. Lets a form show a field error (e.g. "this code is already in use") inline next to
 * the offending input instead of only as a generic toast.
 */
export const getApiFieldErrors = (error: unknown): Record<string, string[]> => {
  if (axios.isAxiosError(error)) {
    return (error.response?.data as { errors?: Record<string, string[]> } | undefined)?.errors ?? {}
  }
  return {}
}

export default http
